import { Numpad, type NumpadKey } from '@mobile/components/kartoy/numpad'
import { PillButton } from '@mobile/components/kartoy/pill-button'
import type { RootRouteProps } from '@mobile/screens'
import { getWithdrawBank, WithdrawBankId } from '@mobile/screens/withdraw-banks/data'
import locales from '@mobile/screens/withdraw-banks/index.i18n.json'
import { useCardRates } from '@shared-core/hooks/use-card-rates'
import {
  formatInputAmount as formatAmount,
  toInputAmount,
  useCurrencyAmount,
} from '@shared-core/hooks/use-currency-amount'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { useWithdrawCardBalance } from '@shared-core/hooks/use-withdraw-card-balance'
import { ScreensList, useNavigation, useRoute } from '@shared-core/navigation'
import { formatWithThousandSeparators } from '@shared-core/utils/balances/balances'
import { Big } from '@shared-core/utils/balances/big'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { FastImage } from '@shared-ui/components/ui/fast-image'
import { Column, Row } from '@shared-ui/components/ui/grid'
import { Header } from '@shared-ui/components/ui/header'
import { ScreenWrapperWithoutScroll } from '@shared-ui/components/ui/screen-wrappers/screen-wrapper'
import { GText } from '@shared-ui/components/ui/text'
import { useHaptic } from '@shared-ui/platform/hooks/use-haptic'
import { Pressable, View } from 'react-native'
import Animated, {
  Easing,
  FadeInDown,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated'
import { useCreateStyles } from './styles'

export function ScreenWithdrawAmount() {
  const styles = useCreateStyles()
  const navigation = useNavigation()
  const route = useRoute<RootRouteProps<ScreensList.WITHDRAW_AMOUNT>>()
  const haptic = useHaptic()
  const { t } = useI18N(locales)
  const bank = getWithdrawBank(route.params?.bank ?? WithdrawBankId.Tbank)
  const conversionAnimation = useSharedValue(0)
  const { withdrawRate: rate } = useCardRates(route.params?.cardId)
  const currencyAmount = useCurrencyAmount(rate, true)
  const { cardId, balance } = useWithdrawCardBalance(route.params?.cardId)
  const isInsufficientBalance = balance !== null && currencyAmount.amountUsd.gt(balance)
  const isActive =
    Boolean(cardId && rate) && balance !== null && currencyAmount.amountUsd.gt(0) && !isInsufficientBalance
  const mainAmount =
    currencyAmount.inputCurrency === 'usd'
      ? `$${formatAmount(currencyAmount.raw) || '0'}`
      : `₽${formatAmount(currencyAmount.raw) || '0'}`
  const convertedAmount =
    currencyAmount.inputCurrency === 'usd'
      ? `₽${formatWithThousandSeparators(currencyAmount.amountRub.toFixed(0))}`
      : `$${formatWithThousandSeparators(currencyAmount.amountUsd.toFixed(2))}`
  const amountsAnimationStyle = useAnimatedStyle(() => ({
    opacity: 1 - conversionAnimation.value * 0.28,
    transform: [{ scale: 1 - conversionAnimation.value * 0.035 }],
  }))
  const swapAnimationStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${conversionAnimation.value * 180}deg` }],
  }))

  // Обрабатывает цифровую клавиатуру с ограничением длины и двух знаков после запятой.
  const handleKey = (key: NumpadKey) => {
    currencyAmount.events.handleKey(key)
  }

  const handleSubmit = () => {
    if (!isActive) return
    haptic('impactLight')
    navigation.navigate(ScreensList.FORM_SHEET_WITHDRAW_CONFIRM, {
      amount: formatWithThousandSeparators(currencyAmount.amountUsd.toFixed(2)),
      convertedAmount: formatWithThousandSeparators(currencyAmount.amountRub.toFixed(0)),
      bank: bank.id,
      cardId,
    })
  }

  const handleConversion = () => {
    if (!rate) return
    haptic('selection')
    conversionAnimation.value = withSequence(
      withTiming(1, { duration: 140, easing: Easing.inOut(Easing.cubic) }),
      withTiming(0, { duration: 220, easing: Easing.out(Easing.cubic) }),
    )
    currencyAmount.events.convert()
  }

  const secondaryAmount = isInsufficientBalance ? (
    <GText style={styles.balanceError}>{t('insufficient-balance')}</GText>
  ) : (
    <Pressable accessibilityRole="button" hitSlop={8} onPress={handleConversion}>
      <Row align="center" gap={4}>
        <AppIcon icon="amount-equal" size={20} />
        <GText fw={600} style={styles.converted}>
          {rate ? convertedAmount : '—'}
        </GText>
        <View style={styles.swapButton}>
          <Animated.View style={swapAnimationStyle}>
            <AppIcon icon="amount-swap" size={16} />
          </Animated.View>
        </View>
      </Row>
    </Pressable>
  )

  const headerTitle = (
    <Animated.View entering={FadeInDown.delay(40).duration(380)} style={styles.headerTitle}>
      <Row align="center" gap={8}>
        <GText fw={600} style={styles.headerText}>
          {t('withdraw-to')}
        </GText>
        <FastImage source={bank.image} style={styles.headerIcon} resizeMode="cover" />
      </Row>
      <GText fw={500} style={styles.rate}>
        {t('rate', { rate: rate ? formatWithThousandSeparators(rate).replace('.', ',') : '—' })}
      </GText>
    </Animated.View>
  )

  return (
    <ScreenWrapperWithoutScroll style={styles.container}>
      <Header isBackButton exitCross={false} title={headerTitle} />
      <Animated.View entering={FadeInDown.delay(100).duration(400)} style={[styles.amountBlock, amountsAnimationStyle]}>
        <GText fw={600} style={styles.amount}>
          {mainAmount}
        </GText>
        {secondaryAmount}
      </Animated.View>
      <Column align="center">
        <Animated.View entering={FadeInDown.delay(160).duration(400)}>
          <Pressable
            accessibilityRole="button"
            disabled={balance === null}
            style={styles.balanceChip}
            onPress={() => {
              currencyAmount.events.setInputCurrency('usd')

              if (balance !== null) currencyAmount.events.setRaw(toInputAmount(Big(balance).round(2, Big.roundDown)))
            }}
          >
            <GText fw={600} style={[styles.balanceText, isInsufficientBalance && styles.balanceErrorText]}>
              {balance !== null ? `$${formatWithThousandSeparators(balance).replace('.', ',')}` : '—'}
            </GText>
          </Pressable>
        </Animated.View>
        <Animated.View entering={FadeInDown.delay(220).duration(400)}>
          <Numpad onPressKey={handleKey} />
        </Animated.View>
        <Animated.View entering={FadeInDown.delay(280).duration(400)} style={styles.cta}>
          <PillButton
            title={isActive ? t('continue') : t('enter-amount')}
            variant={isActive ? 'primary' : 'secondary'}
            disabled={!isActive}
            onPress={handleSubmit}
          />
        </Animated.View>
      </Column>
    </ScreenWrapperWithoutScroll>
  )
}
